import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setActiveOverlay, setPdfOverlay } from "../../../redux/Reducer/HealthMap/HealthMapSlice";
import { fetchHealthMap } from "../../../redux/Thunk/HealthMap/HealthMapThunks";
import { sharePrescriptionOnWhatsapp } from "../../../redux/Thunk/Prescription/PrescriptionThunks";
import { shareReceiptOnWhatsapp } from "../../../redux/Thunk/Receipt/ReceiptThunks";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import HealthMapNoteEditor from "./HealthMapNoteEditor";
import HealthMapReportEditor from "./HealthMapReportEditor";
import HealthMapFileOverlay from "./HealthMapFileOverlay";

const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    const day = d.getDate();
    const month = d.toLocaleString("en-US", { month: "short" });
    const year = d.getFullYear();
    const time = d.toLocaleString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
    return { day, month, year, time, full: `${day} ${month} ${year}` };
};

/* ─── Health Record Icons (matching PatientHealthRecords) ─── */
const RecordIcon = ({ type }) => {
    const icons = {
        Complaints: (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
        ),
        Diagnosis: (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
            </svg>
        ),
        Measurements: (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2v20M2 12h20M7 2v5M17 2v5M7 17v5M17 17v5" />
            </svg>
        ),
        "Tests Prescribed": (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
            </svg>
        ),
        Advice: (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
        ),
        Observations: (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
            </svg>
        ),
        Notes: (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
        ),
        "Next Visit": (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
            </svg>
        ),
        Diagrams: (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" />
            </svg>
        ),
        Medicines: (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.5 1.5l-8.46 8.46a5 5 0 0 0 7.08 7.04l8.46-8.46a5 5 0 0 0-7.08-7.04z" />
                <path d="M6.05 13.54l5.5-5.5" />
            </svg>
        ),
        "Patient Images": (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" />
            </svg>
        ),
        Treatment: (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2v20M2 12h20" />
            </svg>
        ),
    };
    return icons[type] || null;
};

const recordColorMap = {
    Complaints: "hr-complaints",
    Diagnosis: "hr-diagnosis",
    Measurements: "hr-measurements",
    "Tests Prescribed": "hr-tests",
    Advice: "hr-advice",
    Observations: "hr-observations",
    Notes: "hr-notes",
    "Next Visit": "hr-nextvisit",
    Diagrams: "hr-diagrams",
    Medicines: "hr-medicines",
    "Patient Images": "hr-images",
    Treatment: "hr-treatment",
};

/* ─── HealthMap Entry Component with Framer Motion ─── */
const HealthMapEntry = ({ date, item, isOtherDoctor, renderCard }) => {
    const ref = useRef(null);

    const { scrollYProgress } = useScroll({
        target: ref,
        // start 70%: top of element at 70% viewport height
        // start 50%: top of element at 50% viewport height (middle)
        offset: ["start 70%", "start 50%"]
    });

    // Smooth the scroll progress with a spring for "fluidity"
    const smoothProgress = useSpring(scrollYProgress, {
        stiffness: 120,
        damping: 30,
        restDelta: 0.001
    });

    // Rotation + Sliding motion for a "floating" feel
    const rotateX = useTransform(smoothProgress, [0, 1], [15, 0]);
    const y = useTransform(smoothProgress, [0, 1], [20, 0]);

    return (
        <div className="healthmap-entry" ref={ref} style={{ perspective: "1500px" }}>
            <div className="healthmap-date">
                <div className="healthmap-date-day">{date.day} {date.month}</div>
                <div>{date.year} · {date.time}</div>
            </div>
            <motion.div
                className="hm-card-wrapper"
                style={{
                    rotateX,
                    y,
                    transformOrigin: "center center",
                    transformStyle: "preserve-3d",
                    willChange: "transform"
                }}
            >
                {renderCard(item)}
                {isOtherDoctor && (
                    <div className="doctor-tag">🏥 {item.doctorName}</div>
                )}
            </motion.div>
        </div>
    );
};

const HEALTHMAP_FILTER_TYPES = [
    { key: "prescription", label: "Prescriptions", icon: "🩺" },
    { key: "order", label: "Fulfilled Products", icon: "📦" },
    { key: "note", label: "Clinical Notes", icon: "📝" },
    { key: "report", label: "Reports", icon: "📁" },
    { key: "receipt", label: "Receipts", icon: "🧾" },
];

const ALL_FILTER_KEYS = HEALTHMAP_FILTER_TYPES.map((f) => f.key);

const HealthMapView = () => {
    const dispatch = useDispatch();
    const healthMapItems = useSelector((s) => s.healthMap.healthMapItems);
    const selectedPatient = useSelector((s) => s.healthMap.selectedPatient);
    const loading = useSelector((s) => s.healthMap.loading);
    const activeOverlay = useSelector((s) => s.healthMap.activeOverlay);
    const pdfOverlay = useSelector((s) => s.healthMap.pdfOverlay);
    const [previewImage, setPreviewImage] = React.useState(null);
    const doctorDetails = useSelector((s) => s?.doctor?.doctorDetails?.doctor);
    const authUser = useSelector((s) => s?.auth?.user);
    const currentDID = doctorDetails?.DID || authUser?.DID || authUser?.id || localStorage.getItem("DID");
    const doctorName = `${doctorDetails?.personalInfo?.name || ""} ${doctorDetails?.personalInfo?.surname || ""}`.trim();

    // Multi-select Timeline Filter state
    const [selectedFilters, setSelectedFilters] = React.useState(ALL_FILTER_KEYS);
    const [filterDropdownOpen, setFilterDropdownOpen] = React.useState(false);
    const [dropdownAlign, setDropdownAlign] = React.useState("right");
    const filterDropdownRef = useRef(null);

    const handleToggleDropdown = () => {
        if (!filterDropdownOpen && filterDropdownRef.current) {
            const rect = filterDropdownRef.current.getBoundingClientRect();
            if (rect.left < 280) {
                setDropdownAlign("left");
            } else {
                setDropdownAlign("right");
            }
        }
        setFilterDropdownOpen((prev) => !prev);
    };

    const lineRef = useRef(null);
    const containerRef = useRef(null);

    // Global page scroll progress - guaranteed 0 at top, 1 at bottom
    const { scrollYProgress } = useScroll();

    // Invert: 100% height at top (Today), 0% at bottom (Beginning of history)
    const invertedProgress = useTransform(scrollYProgress, [0, 1], [1, 0]);

    const scaleY = useSpring(invertedProgress, {
        stiffness: 100,
        damping: 30,
        restDelta: 0.001
    });

    // Map spring value to height percentage string to prevent distortion of the top dot
    const height = useTransform(scaleY, [0, 1], ["0%", "100%"]);

    useEffect(() => {
        // Force scroll to top on mount to ensure progress line starts at 100%
        window.scrollTo(0, 0);
    }, []);

    // Outside click & escape listener for filter dropdown
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (filterDropdownRef.current && !filterDropdownRef.current.contains(e.target)) {
                setFilterDropdownOpen(false);
            }
        };
        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                setFilterDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, []);

    // Count available items per type for this patient (excluding private receipts of other doctors)
    const countsByType = React.useMemo(() => {
        const counts = {
            prescription: 0,
            order: 0,
            note: 0,
            report: 0,
            receipt: 0,
        };
        healthMapItems.forEach((item) => {
            if (item.type === "receipt" && item.DID !== currentDID) {
                return;
            }
            if (counts[item.type] !== undefined) {
                counts[item.type]++;
            }
        });
        return counts;
    }, [healthMapItems, currentDID]);

    const totalAvailableItems = React.useMemo(() => {
        return Object.values(countsByType).reduce((acc, curr) => acc + curr, 0);
    }, [countsByType]);

    // Filter items: Receipts are private to generating doctor, and apply multi-select filter
    const visibleItems = React.useMemo(() => {
        return healthMapItems.filter((item) => {
            if (item.type === "receipt") {
                if (item.DID !== currentDID) return false;
            }
            return selectedFilters.includes(item.type);
        });
    }, [healthMapItems, currentDID, selectedFilters]);

    const isAllSelected = selectedFilters.length === ALL_FILTER_KEYS.length;
    const isFiltered = !isAllSelected;

    const toggleFilter = (key) => {
        if (selectedFilters.includes(key)) {
            setSelectedFilters(selectedFilters.filter((k) => k !== key));
        } else {
            setSelectedFilters([...selectedFilters, key]);
        }
    };

    const handleSelectAll = () => {
        setSelectedFilters(ALL_FILTER_KEYS);
    };

    const handleClearAll = () => {
        setSelectedFilters([]);
    };


    const openPdf = (url, type, id) => {
        dispatch(setPdfOverlay({ url, type, id }));
    };

    const handleSharePrescription = async (d) => {
        const shareData = {
            XID: d.XID,
            patientPhoneNumber: selectedPatient?.phoneNumber,
            patientName: `${d.patientName || ""} ${d.patientSurname || ""}`.trim(),
            doctorName,
            pdfUrl: d.pdfUrl,
        };
        await dispatch(sharePrescriptionOnWhatsapp(shareData));
    };

    const handleShareReceipt = async (d) => {
        const shareData = {
            RID: d.RID,
            patientPhoneNumber: selectedPatient?.phoneNumber,
            patientName: `${d.patientName || ""} ${d.patientSurname || ""}`.trim(),
            doctorName,
            pdfUrl: d.pdfUrl,
        };
        await dispatch(shareReceiptOnWhatsapp(shareData));
    };

    const formatDosage = (p) => {
        if (p.isSOS || p.dosageMode === "SOS") return "SOS (As Needed)";
        if (p.isNA || p.dosageMode === "NA") return "NA";
        if (p.isPeriodic || p.dosageMode === "PERIODIC") {
            const freq = p.periodicFrequency || "Periodic";
            const doseVal = (p.morningTime || p.midDay || p.eveningTime || p.night || 1);
            return `${freq}${doseVal > 1 ? ` (${doseVal} qty)` : ''}`;
        }
        if (p.isReducingDose || p.dosageMode === "REDUCING") {
            const steps = Array.isArray(p.reducingDoses) ? p.reducingDoses : [];
            if (steps.length > 0) {
                return "Reducing: " + steps.map(s => `Days ${s.startDay}-${s.endDay}: ${s.morningTime || 0}-${s.midDay || 0}-${s.eveningTime || 0}-${s.night || 0}`).join("; ");
            }
            return "Reducing Dose";
        }
        const parts = [p.morningTime || 0, p.midDay || 0, p.eveningTime || 0, p.night || 0];
        const dosageStr = parts.join("-");
        const timing = p.takenTime ? ` (${p.takenTime})` : "";
        return dosageStr + timing;
    };

    /* ─── Build health records array from prescription data ─── */
    const getHealthRecords = (d) => {
        const records = [];
        const mapItems = (arr) => (arr || []).map(m => typeof m === "string" ? m : `${m.parameter}${m.value ? `: ${m.value}` : ""}`).filter(Boolean);

        if (d.complaints?.length > 0) records.push({ label: "Complaints", pills: mapItems(d.complaints) });
        if (d.diagnosis?.length > 0) records.push({ label: "Diagnosis", pills: mapItems(d.diagnosis) });
        if (d.measurements?.length > 0) records.push({ label: "Measurements", pills: mapItems(d.measurements) });
        if (d.testsPrescribed?.length > 0) records.push({ label: "Tests Prescribed", pills: mapItems(d.testsPrescribed) });
        if (d.advice?.length > 0) records.push({ label: "Advice", pills: mapItems(d.advice) });
        if (d.observations?.length > 0) records.push({ label: "Observations", pills: mapItems(d.observations) });
        if (d.treatments?.length > 0) records.push({ label: "Treatment", pills: mapItems(d.treatments) });
        if (d.diagrams?.length > 0) records.push({ label: "Diagrams", items: d.diagrams });
        if (d.images?.length > 0) records.push({ label: "Patient Images", photos: d.images });
        if (d.notes?.trim()) records.push({ label: "Notes", text: d.notes });
        if (d.nextVisit) records.push({ label: "Next Visit", text: `${d.nextVisit} days` });
        return records;
    };

    const renderPrescriptionCard = (item) => {
        const d = item.data;
        const products = d.products || [];
        const healthRecords = getHealthRecords(d);

        return (
            <div className="hm-card hm-card-clickable prescription" onClick={() => d.pdfUrl && openPdf(d.pdfUrl, "prescription", d.XID)}>
                <div className="hm-card-header">
                    <span className="hm-card-type prescription">🩺 Prescription</span>
                    <span className="hm-card-id">{d.XID}</span>
                </div>
                <div className="hm-card-body">
                    {/* Health Records with icons */}
                    {healthRecords.length > 0 && (
                        <div className="hm-health-records">
                            {healthRecords.map((rec, i) => (
                                <div key={i} className={`hm-hr-item ${recordColorMap[rec.label] || ""}`}>
                                    <div className="hm-hr-header">
                                        <span className="hm-hr-icon"><RecordIcon type={rec.label} /></span>
                                        <span className="hm-hr-label">{rec.label}</span>
                                    </div>
                                    {rec.pills ? (
                                        <div className="hm-hr-pills">
                                            {rec.pills.map((pill, j) => (
                                                <span key={j} className="hm-hr-pill">{pill}</span>
                                            ))}
                                        </div>
                                    ) : rec.items ? (
                                        <div className="hm-diagram-grid">
                                            {rec.items.map((diag, j) => (
                                                <div
                                                    key={j}
                                                    className="hm-diagram-preview"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setPreviewImage({
                                                            url: diag.imageUrl,
                                                            title: diag.parameter,
                                                            subtitle: diag.subtitle,
                                                            value: diag.value
                                                        });
                                                    }}
                                                >
                                                    <img src={diag.imageUrl} alt={diag.parameter} />
                                                    <div className="hm-diagram-info">
                                                        <div className="hm-diag-meta">
                                                            <span className="hm-diag-title">{diag.parameter}</span>
                                                            {diag.subtitle && <span className="hm-diag-sub">{diag.subtitle}</span>}
                                                            {diag.value && <span className="hm-diag-val">{diag.value}</span>}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : rec.photos ? (
                                        <div className="hm-diagram-grid">
                                            {rec.photos.map((img, j) => (
                                                <div
                                                    key={j}
                                                    className="hm-diagram-preview patient-image"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setPreviewImage({
                                                            url: img.imageUrl,
                                                            title: "Clinical Photo",
                                                            value: img.note
                                                        });
                                                    }}
                                                >
                                                    <img src={img.imageUrl} alt="Clinical record" />
                                                    {img.note && (
                                                        <div className="hm-diagram-info">
                                                            <div className="hm-diag-meta">
                                                                <span className="hm-diag-val">{img.note}</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <span className="hm-hr-text">{rec.text}</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                    {/* Medicines tile */}
                    {products.length > 0 && (
                        <div className="hm-health-records">
                            <div className={`hm-hr-item ${recordColorMap["Medicines"]}`}>
                                <div className="hm-hr-header">
                                    <span className="hm-hr-icon"><RecordIcon type="Medicines" /></span>
                                    <span className="hm-hr-label">Medicines ({products.length})</span>
                                </div>
                                <div className="hm-hr-pills medicine-list">
                                    {products.map((p, i) => (
                                        <div key={i} className="hm-medicine-row">
                                            <span className="hm-med-name">{p.productName}</span>
                                            <span className="hm-med-details">
                                                <span className="hm-med-dosage">{formatDosage(p)}</span>
                                                {p.rxDays > 0 && <span className="hm-med-days">{p.rxDays} Days</span>}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderReceiptCard = (item) => {
        const d = item.data;
        return (
            <div className="hm-card hm-card-clickable receipt" onClick={() => d.pdfUrl && openPdf(d.pdfUrl, "receipt", d.RID)}>
                <div className="hm-card-header">
                    <span className="hm-card-type receipt">🧾 Receipt</span>
                    <span className="hm-card-id">{d.RID}</span>
                </div>
                <div className="hm-card-body">
                    {d.services && (
                        <div className="hm-card-row">
                            <span className="hm-card-label">Services:</span>
                            <span className="hm-card-value">{d.services}</span>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderOrderCard = (item) => {
        const d = item.data;
        const products = d.products || [];
        const statusClass = (d.orderStatus || "").toLowerCase();
        return (
            <div className="hm-card order">
                <div className="hm-card-header">
                    <span className="hm-card-type order">📦 Fulfilled Products</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span className={`order-status ${statusClass}`}>{d.orderStatus || "Pending"}</span>
                        <span className="hm-card-id">{d.OID}</span>
                    </div>
                </div>
                <div className="hm-card-body">
                    {products.length > 0 && (
                        <div className="hm-card-products">
                            {products.map((p, i) => (
                                <span key={i} className="hm-product-pill">
                                    {p.productName} × {p.orderQty}
                                </span>
                            ))}
                        </div>
                    )}
                    {d.isCancelled && d.cancelReason && (
                        <div className="hm-card-row" style={{ marginTop: 4 }}>
                            <span className="hm-card-label">Reason:</span>
                            <span className="hm-card-value">{d.cancelReason}</span>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderNoteCard = (item) => {
        const isOwner = item.DID === currentDID;
        return (
            <div className="hm-card record">
                <HealthMapNoteEditor
                    record={item}
                    PID={selectedPatient?.PID}
                    isOwner={isOwner}
                    onImageClick={(url, title, subtitle, value) => setPreviewImage({ url, title, subtitle, value })}
                    onDone={() => dispatch(fetchHealthMap(selectedPatient?.PID))}
                />
            </div>
        );
    };

    const renderCard = (item) => {
        switch (item.type) {
            case "prescription": return renderPrescriptionCard(item);
            case "receipt": return renderReceiptCard(item);
            case "order": return renderOrderCard(item);
            case "note": return renderNoteCard(item);
            case "report": return renderReportCard(item);
            default: return null;
        }
    };

    const renderReportCard = (item) => {
        const isOwner = item.DID === currentDID;
        return (
            <div className="hm-card report">
                <HealthMapReportEditor
                    record={item}
                    PID={selectedPatient?.PID}
                    isOwner={isOwner}
                    onFileClick={(url, title) => dispatch(setPdfOverlay({ url, type: "report", id: item.id, title }))}
                    onDone={() => dispatch(fetchHealthMap(selectedPatient?.PID))}
                />
            </div>
        );
    };

    if (loading) {
        return (
            <div className="healthmap-container">
                {[1, 2, 3, 4].map((i) => <div key={i} className="hm-skeleton" />)}
            </div>
        );
    }

    return (
        <div className="healthmap-container" ref={containerRef}>
            <div className="healthmap-list">
                {/* Progress track (background) */}
                <div className="healthmap-line" ref={lineRef} />
                {/* Progress fill (active) - Physics based smooth motion */}
                <motion.div className="healthmap-line-fill" style={{ height }} />

                {/* Today entry */}
                <div className={`healthmap-entry today-entry ${filterDropdownOpen ? "filter-open" : ""}`}>
                    <div className="healthmap-date">
                        <div className="healthmap-date-day">Today</div>
                    </div>
                    <div className="today-milestone">
                        <div className="today-header">
                            <div className="healthmap-integrated-logo">
                                <span className="brand-primary">HealthMap</span>&nbsp;
                                <span className="brand-secondary">by Hyperapt</span>
                            </div>
                            <span className="header-divider">|</span>
                            <span className="today-label">What's next?</span>
                        </div>
                        <div className="today-actions">
                            <button className="today-action-btn" onClick={() => dispatch(setActiveOverlay("prescription"))}>
                                🩺 New Prescription
                            </button>
                            <button className="today-action-btn" onClick={() => dispatch(setActiveOverlay("receipt"))}>
                                🧾 New Receipt
                            </button>
                            <button className="today-action-btn" onClick={() => dispatch(setActiveOverlay("note"))}>
                                📝 Add Clinical Note
                            </button>
                            <button className="today-action-btn" onClick={() => dispatch(setActiveOverlay("report"))}>
                                📁 Add Report
                            </button>

                            {/* Multi-select Timeline Filter Dropdown */}
                            <div className="today-filter-container" ref={filterDropdownRef}>
                                <button
                                    type="button"
                                    className={`today-filter-btn ${isFiltered ? "is-filtered" : ""} ${filterDropdownOpen ? "open" : ""}`}
                                    onClick={handleToggleDropdown}
                                    aria-expanded={filterDropdownOpen}
                                    title="Filter timeline entries by record type"
                                >
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="today-filter-icon">
                                        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                                    </svg>
                                    <span>Filters</span>
                                    {isFiltered ? (
                                        <span className="today-filter-badge filtered">
                                            {selectedFilters.length}/{ALL_FILTER_KEYS.length}
                                        </span>
                                    ) : (
                                        <span className="today-filter-badge all">All</span>
                                    )}
                                    <span className={`today-filter-chevron ${filterDropdownOpen ? "open" : ""}`}>▼</span>
                                </button>

                                {filterDropdownOpen && (
                                    <div className={`today-filter-dropdown align-${dropdownAlign}`} onClick={(e) => e.stopPropagation()}>
                                        <div className="today-filter-header">
                                            <div className="today-filter-title">Filter Timeline</div>
                                            {isAllSelected ? (
                                                <button
                                                    type="button"
                                                    className="today-filter-toggle-btn"
                                                    onClick={handleClearAll}
                                                >
                                                    Deselect All
                                                </button>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="today-filter-toggle-btn"
                                                    onClick={handleSelectAll}
                                                >
                                                    Select All
                                                </button>
                                            )}
                                        </div>

                                        <div className="today-filter-list">
                                            {HEALTHMAP_FILTER_TYPES.map((filter) => {
                                                const isSelected = selectedFilters.includes(filter.key);
                                                const count = countsByType[filter.key] || 0;
                                                return (
                                                    <div
                                                        key={filter.key}
                                                        className={`today-filter-item ${isSelected ? "selected" : ""}`}
                                                        onClick={() => toggleFilter(filter.key)}
                                                    >
                                                        <div className="today-filter-item-left">
                                                            <div className={`today-filter-checkbox ${isSelected ? "checked" : ""}`}>
                                                                {isSelected && (
                                                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
                                                                        <polyline points="20 6 9 17 4 12" />
                                                                    </svg>
                                                                )}
                                                            </div>
                                                            <span className="today-filter-item-icon">{filter.icon}</span>
                                                            <span className="today-filter-item-label">{filter.label}</span>
                                                        </div>
                                                        <span className={`today-filter-item-count ${count === 0 ? "zero" : ""}`}>
                                                            {count}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        <div className="today-filter-footer">
                                            <span className="today-filter-footer-text">
                                                Showing {visibleItems.length} of {totalAvailableItems} records
                                            </span>
                                            {isFiltered && (
                                                <button
                                                    type="button"
                                                    className="today-filter-reset-link"
                                                    onClick={handleSelectAll}
                                                >
                                                    Reset
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* HealthMap entries */}
                {visibleItems.length === 0 && (
                    <div className="healthmap-empty">
                        <div className="healthmap-empty-icon">{totalAvailableItems > 0 ? "🔍" : "📋"}</div>
                        <div className="healthmap-empty-text">
                            {totalAvailableItems > 0 ? "No matching records" : "No records yet"}
                        </div>
                        <div className="healthmap-empty-sub">
                            {totalAvailableItems > 0
                                ? "No timeline items match the currently selected filter(s)."
                                : "Start by creating a prescription, receipt, or clinical note above."}
                        </div>
                        {totalAvailableItems > 0 && (
                            <button
                                type="button"
                                className="today-action-btn"
                                style={{ marginTop: 16 }}
                                onClick={handleSelectAll}
                            >
                                ↺ Reset Filters to Show All
                            </button>
                        )}
                    </div>
                )}

                {visibleItems.map((item, idx) => {
                    const date = formatDate(item.createdAt);
                    const isOtherDoctor = item.DID !== currentDID;
                    return (
                        <HealthMapEntry
                            key={`${item.type}-${item.id}-${idx}`}
                            date={date}
                            item={item}
                            isOtherDoctor={isOtherDoctor}
                            renderCard={renderCard}
                        />
                    );
                })}
            </div>

            {/* Clinical note overlay */}
            {activeOverlay === "note" && (
                <div className="hm-overlay" onClick={() => dispatch(setActiveOverlay(null))}>
                    <div className="hm-overlay-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 540 }}>
                        <button className="hm-overlay-close" onClick={() => dispatch(setActiveOverlay(null))}>✕</button>
                        <div className="hm-overlay-title">📝 New Clinical Note</div>
                        <div className="hm-overlay-body">
                            <HealthMapNoteEditor
                                isNew
                                PID={selectedPatient?.PID}
                                onImageClick={(url, title, subtitle, value) => setPreviewImage({ url, title, subtitle, value })}
                                onDone={() => dispatch(fetchHealthMap(selectedPatient?.PID))}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Image Preview Overlay */}
            {previewImage && (
                <div className="hm-overlay" onClick={() => setPreviewImage(null)}>
                    <div className="hm-overlay-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "min(90vw, 800px)", background: "#1a1a1a", padding: "12px", borderRadius: "24px", border: "1px solid rgba(255,255,255,0.12)", boxShadow: "0 25px 50px -12px rgba(0,0,0,0.7)" }}>
                        <button className="hm-overlay-close" onClick={() => setPreviewImage(null)} style={{ top: -45, right: 0 }}>✕</button>
                        <div style={{ display: "flex", flexDirection: "column", overflow: "hidden", borderRadius: "16px" }}>
                            <img
                                src={previewImage.url}
                                alt={previewImage.title}
                                style={{ width: "100%", height: "auto", display: "block" }}
                            />
                            {(previewImage.title || previewImage.subtitle || previewImage.value) && (
                                <div style={{ color: "#fff", textAlign: "center", display: "flex", flexDirection: "column", gap: 2, background: "rgba(255,255,255,0.03)", padding: "16px 12px" }}>
                                    {previewImage.title && <div style={{ fontSize: 18, fontWeight: 700 }}>{previewImage.title}</div>}
                                    {previewImage.subtitle && (
                                        <div style={{ fontSize: 14, opacity: 0.8 }}>{previewImage.subtitle}</div>
                                    )}
                                    {previewImage.value && (
                                        <div style={{ fontSize: 13, background: "rgba(255,255,255,0.05)", padding: "8px 16px", borderRadius: 8, marginTop: 8, border: "1px solid rgba(255,255,255,0.08)" }}>
                                            <span style={{ opacity: 0.6, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, display: "block", marginBottom: 2 }}>Note</span>
                                            {previewImage.value}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* File Overlay (Prescription, Receipt, Report) */}
            {pdfOverlay && (() => {
                const shareHandler = () => {
                    const item = healthMapItems.find(i => {
                        if (pdfOverlay.type === "prescription") return i.type === "prescription" && i.data?.XID === pdfOverlay.id;
                        if (pdfOverlay.type === "receipt") return i.type === "receipt" && i.data?.RID === pdfOverlay.id;
                        return false;
                    });
                    if (!item) return;
                    if (pdfOverlay.type === "prescription") handleSharePrescription(item.data);
                    if (pdfOverlay.type === "receipt") handleShareReceipt(item.data);
                };
                return (
                    <HealthMapFileOverlay
                        url={pdfOverlay.url}
                        type={pdfOverlay.type}
                        title={pdfOverlay.title}
                        id={pdfOverlay.id}
                        onClose={() => dispatch(setPdfOverlay(null))}
                        onShare={(pdfOverlay.type === "prescription" || pdfOverlay.type === "receipt") ? shareHandler : null}
                    />
                );
            })()}
            {/* Report overlay */}
            {activeOverlay === "report" && (
                <div className="hm-overlay" onClick={() => dispatch(setActiveOverlay(null))}>
                    <div className="hm-overlay-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 540 }}>
                        <button className="hm-overlay-close" onClick={() => dispatch(setActiveOverlay(null))}>✕</button>
                        <div className="hm-overlay-title">📁 Add Clinical Report</div>
                        <div className="hm-overlay-body">
                            <HealthMapReportEditor
                                PID={selectedPatient?.PID}
                                isNew={true}
                                onDone={() => dispatch(fetchHealthMap(selectedPatient?.PID))}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HealthMapView;
